import React, { useCallback, useEffect, useState } from 'react';

import {
    useDispatch,
    useSelector,
} from 'react-redux';

import interactionResponse from 'await-interaction-response';

import { Tab, Tabs } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { searchCategoryClick } from 'reaxl-analytics-handlers';

import { categoryOptions } from '@/utilities/searchCategoriesUtils';

import {
    srpActiveInteractionDuck,
    srpFiltersDuck,
} from '@/ducks/srp';

import useSrpNavigation from '@/hooks/useSrpNavigation';

function filtersValuesRetentionFiltering({ filtersValues, categoryFilterNames }) {
    const retainedFilters = !!filtersValues && Object.fromEntries(Object.entries(filtersValues).filter(([key]) => !categoryFilterNames.includes(key)));
    const sanitizedFiltersValues = !!retainedFilters && Object.fromEntries(Object.entries(retainedFilters).map(([key, value]) => [key.split('|')[0], value]));

    return sanitizedFiltersValues;
}

export default function CategorySearchContainer() {
    const dispatch = useDispatch();
    const navigateToSrp = useSrpNavigation();
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);
    const activeCategory = useSelector(srpActiveInteractionDuck.selectors.getActiveCategory);
    const [activeKey, setActiveKey] = useState('');

    useEffect(() => {
        if (activeCategory !== activeKey) {
            setActiveKey(activeCategory);
        }
    }, [activeCategory, activeKey, setActiveKey]);

    const categoryFilters = categoryOptions.filter((category) => category.filterName !== 'allResults');
    const categoryFilterNames = categoryFilters.map((category) => category.filterName);

    const allResultsFilterClear = useCallback(async () => {
        const keys = Object.keys(filtersValues).filter((key) => !categoryFilterNames.includes(key));
        const additionalFilters = filtersValuesRetentionFiltering({ filtersValues, categoryFilterNames });

        dispatch(srpFiltersDuck.creators.clearFilters({ keys }));

        await navigateToSrp({
            resetPagination: true,
            isNewSearch: true,
            filtersValues: { ...additionalFilters },
            action: 'replace',
        });

    }, [dispatch, filtersValues, navigateToSrp, categoryFilterNames]);

    const handleFilterApply = useCallback(async (selectedCategoryValue) => {
        await interactionResponse();

        const retainValues = filtersValuesRetentionFiltering({ filtersValues, categoryFilterNames });

        if (selectedCategoryValue === 'allResults') {
            allResultsFilterClear();
        }

        if (selectedCategoryValue !== 'allResults') {
            const newFiltersValues = {
                ...selectedCategoryValue,
                ...retainValues,
                experience: null,
                query: {},
            };

            await navigateToSrp({
                resetPagination: true,
                isNewSearch: true,
                filtersValues: { ...newFiltersValues },
                action: 'replace',
            });
        }
    }, [navigateToSrp, allResultsFilterClear, filtersValues, categoryFilterNames]);

    const handleTabSelect = (eventKey) => {
        sendClick(searchCategoryClick, {}, { categoryName: eventKey });
        const selectedCategory = categoryOptions.find((option) => option.label === eventKey);

        // suppress selection of filter is already applied
        if (eventKey === activeCategory) {
            return null;
        }

        if (selectedCategory) {
            setActiveKey(eventKey);
            dispatch(srpActiveInteractionDuck.creators.setKeys({
                activeCategory: eventKey,
            }));

            handleFilterApply(selectedCategory.value);
        }
        return null;
    };

    const renderCategoryTabs = () => categoryOptions.map((category, index) => {
        const { label, icon, value } = category;
        return (
            <Tab
                id={value}
                eventKey={label}
                key={`${index}-${label}`}
                title={label}
                icon={icon}
            />
        );
    });

    return (
        <Tabs
            id="categorySearchContainer"
            defaultActiveKey={activeCategory}
            activeKey={activeKey}
            bsStyle="tabs"
            size="xl"
            uiContext="light"
            alignment="left"
            onSelect={handleTabSelect}
        >
            {renderCategoryTabs(categoryOptions)}
        </Tabs>
    );
}
